import React, { useState, useEffect } from 'react';
import {
	Box,
	Text,
	Progress,
	Button,
	Icon,
	Alert,
	AlertIcon,
	Spinner,
	HStack,
	VStack,
	Center,
	Divider,
	Badge,
	Heading,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
	useColorModeValue,
} from '@chakra-ui/react';
import { FaArrowUp } from 'react-icons/fa';
import { usePlanFeatureAccess } from '../../../hooks/usePlanFeatureAccess';
import { normalizeFeatureKey } from '../../../utils/featureUtils';
import { usePlans } from '../../../hooks/usePlan.js';
import { useSubscription } from '../../../hooks/useSubscription.js';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext.jsx';
import SubscriptionPlanMatrix from '../../Auth/SubscriptionPlan/SubscriptionPlanMatrix.jsx';
import SubscriptionModal from '../../Auth/SubscriptionPlan/EmbeddedCheckout/SubscriptionModal.jsx';

/**
 * PlanUsageBar
 *
 * Reusable component that shows a progress bar for a plan feature limit,
 * warning/error alerts with an inline "Upgrade Plan" button, and opens a
 * full plan-selection + Stripe checkout modal flow on click.
 *
 * Props:
 *   featureKey        {string}   – featureUniqueKey as stored in the Feature collection
 *   currentUsage      {number}   – current usage count to compare against the limit
 *   label             {string}   – title shown above the progress bar  (default: 'Usage')
 *   unitLabel         {string}   – unit appended to counts              (default: 'items')
 *   upgradeTitle      {string}   – heading inside the upgrade modal
 *   upgradeDescription{string}   – subtext inside the upgrade modal
 *   onLimitChange     {function} – optional callback ({ numericLimit, isUnlimited, isLoading, hasSubscription })
 *   showUpgradeButton {boolean}  – whether to show the inline upgrade button (default: true)
 *   mb                {number|string} – marginBottom for the wrapper   (default: 6)
 */
const PlanUsageBar = ({
	featureKey,
	currentUsage = 0,
	label = 'Usage',
	unitLabel = 'items',
	upgradeTitle = 'Upgrade your plan',
	upgradeDescription = 'Choose a plan below to increase your allowance.',
	onLimitChange,
	hideWhenHealthy = false,
	healthyThreshold = 0.75,
	showLabel = true,
	showUpgradeButton = true,
	mb = 6,
}) => {
	const { companyId } = useAuthenticatedUserInfo();
	const {
		getFeatureLimit,
		isLoading: limitLoading,
	} = usePlanFeatureAccess();
	const {
		numericLimit,
		isUnlimited,
		hasSubscription,
		isEnforcementActive,
	} = getFeatureLimit(normalizeFeatureKey(featureKey));

	const { data: plans, isLoading: loadingPlans } = usePlans();
	const { data: subscription, refetch: refetchSubscription } =
		useSubscription(companyId);

	const [isYearly, setIsYearly] = useState(false);
	const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);

	const {
		isOpen: isUpgradeOpen,
		onOpen: onUpgradeOpen,
		onClose: onUpgradeClose,
	} = useDisclosure();
	const {
		isOpen: isCheckoutOpen,
		onOpen: onCheckoutOpen,
		onClose: onCheckoutClose,
	} = useDisclosure();

	// Notify parent of resolved limit values whenever they change
	useEffect(() => {
		if (onLimitChange) {
			onLimitChange({
				numericLimit,
				isUnlimited,
				isLoading: limitLoading,
				hasSubscription,
			});
		}
	}, [numericLimit, isUnlimited, limitLoading, hasSubscription, onLimitChange]);

	const handleSelectPlan = (plan) => {
		setSelectedUpgradePlan({
			...plan,
			stripePriceId: isYearly
				? plan.stripePriceIdYearly
				: plan.stripePriceIdMonthly,
		});
		onCheckoutOpen();
	};

	const handleUpgradeSuccess = () => {
		onCheckoutClose();
		refetchSubscription();
		onUpgradeClose();
	};

	const bg = useColorModeValue('white', 'gray.800');
	const borderColor = useColorModeValue('gray.200', 'gray.700');

	// Admin disabled enforcement for this feature
	if (!limitLoading && isEnforcementActive === false) return null;

	// Don't render anything for unlimited plans (once loading is done)
	if (!limitLoading && (isUnlimited || numericLimit === null)) return null;

	const ratio = numericLimit ? currentUsage / numericLimit : 0;

	// Don't render when healthy if caller only wants near-limit visibility
	if (!limitLoading && hideWhenHealthy && ratio < healthyThreshold) return null;

	// Resolve label — supports plain string or { normal, warning, critical } object
	const resolvedLabel =
		typeof label === 'object'
			? ratio >= 1
				? label.critical
				: ratio >= healthyThreshold
					? label.warning
					: label.normal
			: label;

	return (
		<>
			<Box
				width={'100%'}
				bg={ratio >= 1 ? 'red.50' : bg}
				borderWidth='1px'
				borderColor={ratio >= 1 ? 'red.300' : borderColor}
				borderRadius='lg'
				p={4}
				mb={mb}
				boxShadow='sm'>
				{limitLoading ? (
					<HStack spacing={3}>
						<Spinner size='sm' color='teal.500' />
						<Text fontSize='sm' color='gray.500'>
							Checking your {resolvedLabel.toLowerCase()}...
						</Text>
					</HStack>
				) : (
					<HStack spacing={4} align='center'>
						{showLabel && (
							<Text
								fontSize='sm'
								fontWeight='semibold'
								color={
									ratio >= 1
										? 'gray.500'
										: ratio >= healthyThreshold
											? 'orange.600'
											: 'gray.600'
								}
								flexShrink={0}>
								{resolvedLabel}
							</Text>
						)}
						<Progress
							flex={1}
							value={Math.min(ratio * 100, 100)}
							size='sm'
							colorScheme={ratio >= 0.9 ? 'red' : ratio >= 0.75 ? 'orange' : 'teal'}
							borderRadius='md'
						/>
						<Text fontSize='sm' color='gray.600' flexShrink={0} whiteSpace='nowrap'>
							{currentUsage.toLocaleString()} / {numericLimit.toLocaleString()}{' '}
							{unitLabel}
						</Text>
						{showUpgradeButton && ratio >= 0.75 && (
							<Button
								size='xs'
								colorScheme={ratio >= 1 ? 'red' : 'orange'}
								leftIcon={<Icon as={FaArrowUp} />}
								onClick={onUpgradeOpen}
								flexShrink={0}>
								Upgrade
							</Button>
						)}
					</HStack>
				)}
			</Box>

			{/* Upgrade Plan Modal */}
			<Modal
				isOpen={isUpgradeOpen}
				onClose={onUpgradeClose}
				size='5xl'
				isCentered
				scrollBehavior='inside'>
				<ModalOverlay bg='blackAlpha.700' />
				<ModalContent mx={4} maxH='90vh'>
					<ModalCloseButton />
					<ModalBody pb={8} pt={8}>
						<VStack spacing={6} align='stretch'>
							<Center>
								<VStack spacing={3}>
									<Box p={4} bg='orange.50' borderRadius='full'>
										<Icon as={FaArrowUp} boxSize={8} color='orange.500' />
									</Box>
									<Heading as='h2' size='md' textAlign='center' color='gray.800'>
										{upgradeTitle}
									</Heading>
									{subscription?.planId && plans && (
										<Badge colorScheme='blue' fontSize='sm' px={3} py={1}>
											Current Plan:{' '}
											{plans.find((p) => p._id === subscription.planId)?.planName ||
												'Unknown'}
										</Badge>
									)}
									{numericLimit !== null && (
										<Badge colorScheme='gray' fontSize='sm' px={3} py={1}>
											Current limit: {numericLimit.toLocaleString()} {unitLabel}
										</Badge>
									)}
									<Text fontSize='sm' textAlign='center' color='gray.500' maxW='460px'>
										{upgradeDescription}
									</Text>
								</VStack>
							</Center>

							<Divider />

							{/* Billing toggle */}
							<Center>
								<HStack
									spacing={0}
									borderRadius='full'
									overflow='hidden'
									border='1px'
									borderColor='gray.200'
									maxW='320px'
									w='full'
									position='relative'>
									<Box
										position='absolute'
										top={0}
										left={isYearly ? '50%' : '0'}
										w='50%'
										h='full'
										bg='orange.400'
										borderRadius='full'
										transition='left 0.3s ease'
										zIndex={1}
									/>
									<Box
										as='button'
										flex={1}
										py={2}
										px={4}
										color={isYearly ? 'gray.600' : 'white'}
										fontWeight={isYearly ? 'normal' : 'semibold'}
										fontSize='sm'
										cursor='pointer'
										position='relative'
										zIndex={2}
										transition='color 0.3s'
										onClick={() => setIsYearly(false)}>
										Monthly
									</Box>
									<Box
										as='button'
										flex={1}
										py={2}
										px={4}
										color={!isYearly ? 'gray.600' : 'white'}
										fontWeight={!isYearly ? 'normal' : 'semibold'}
										fontSize='sm'
										cursor='pointer'
										position='relative'
										zIndex={2}
										transition='color 0.3s'
										onClick={() => setIsYearly(true)}>
										Annual
									</Box>
								</HStack>
							</Center>

							{/* Plan matrix */}
							{loadingPlans ? (
								<Center py={8}>
									<VStack spacing={3}>
										<Spinner size='lg' color='blue.500' />
										<Text fontSize='sm' color='gray.500'>
											Loading plans...
										</Text>
									</VStack>
								</Center>
							) : (
								<Box
									w='full'
									overflowX='auto'
									overflowY='auto'
									maxH='420px'
									borderRadius='lg'
									border='1px'
									borderColor='gray.200'
									boxShadow='sm'>
									<SubscriptionPlanMatrix
										plans={plans || []}
										onSelectPlan={handleSelectPlan}
										showTitle={true}
										showPrice={true}
										isYearly={isYearly}
										bgColor='white'
										currentSubscription={subscription}
									/>
								</Box>
							)}

							<Alert status='info' borderRadius='md' fontSize='sm'>
								<AlertIcon />
								Upgrade now and get instant access to all features included in your new
								plan.
							</Alert>
						</VStack>
					</ModalBody>
				</ModalContent>
			</Modal>

			{/* Checkout Modal */}
			<SubscriptionModal
				isOpen={isCheckoutOpen}
				onClose={onCheckoutClose}
				plan={selectedUpgradePlan}
				isYearly={isYearly}
				onSubscriptionSuccess={handleUpgradeSuccess}
				currentSubscription={subscription}
				isUpgrade={!!subscription?.hasActiveSubscription}
			/>
		</>
	);
};

export default PlanUsageBar;
